/**
 * Cobros x Mayor — pantalla unificada de cobro y mantenimiento de recibos.
 *
 * La vista principal es el mantenimiento de cobros embebido (ares9740:
 * recibos por fechas, anular — revierte las cuotas — y reimprimir).
 * Tarjetas de acción:
 *   - "Cobrar x mayor" (ares9715): aplica cobros a cuotas pendientes con
 *     distribución multi-factura, en diálogo a pantalla completa.
 *   - "Informe de cobros" (ares9750): cobros aplicados por fechas/cliente
 *     con export (diálogo con barra Volver).
 *   - "Informe de ingresos" (ares9750): ventas al contado + cobros a
 *     crédito consolidados por cliente, forma de pago y día.
 *
 * Sustituye las entradas de menú de cobrosxmayor.js,
 * mantenimientocobrosxmayor.js y listadocobrosxmayor.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import CobrosXmayor from "modules/ventasxmayor/cobrosxmayor";
import MantenimientoCobrosXmayor from "modules/ventasxmayor/mantenimientocobrosxmayor";
import ListadoCobrosXmayor from "modules/ventasxmayor/listadocobrosxmayor";
import IngresosXmayor from "modules/ventasxmayor/ingresosxmayor";
import useAccionInicial from "lib/useAccionInicial";

export default function XmayorCobros() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeCobrar = tienePermisosModulo(roleDetalles, "ares9715");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares9740");
  const puedeInforme = tienePermisosModulo(roleDetalles, "ares9750");

  const [cobroOpen, setCobroOpen] = useState(false);
  const [informeOpen, setInformeOpen] = useState(false);
  const [ingresosOpen, setIngresosOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    cobrar: { permitido: puedeCobrar, abrir: () => setCobroOpen(true) },
    informe: { permitido: puedeInforme, abrir: () => setInformeOpen(true) },
    ingresos: { permitido: puedeInforme, abrir: () => setIngresosOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras aplicar un cobro).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cobros x Mayor"
        subtitle="Cobra las facturas al por mayor y gestiona los recibos en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeCobrar || puedeInforme) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeCobrar && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={PaymentsOutlinedIcon}
                  titulo="Cobrar x mayor"
                  descripcion="Aplica cobros a las cuotas pendientes, incluso de varias facturas"
                  color={FP.color.success}
                  onClick={() => setCobroOpen(true)}
                />
              </Grid>
            )}
            {puedeInforme && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Informe de cobros"
                  descripcion="Cobros aplicados por fechas y cliente, con export"
                  color={FP.color.accentBlue}
                  onClick={() => setInformeOpen(true)}
                />
              </Grid>
            )}
            {puedeInforme && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={QueryStatsOutlinedIcon}
                  titulo="Informe de ingresos"
                  descripcion="Ventas al contado y cobros a crédito, por cliente, forma de pago y día"
                  color={FP.color.navy}
                  onClick={() => setIngresosOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: mantenimiento de recibos (anular/reimprimir) */}
        {puedeMantenimiento && <MantenimientoCobrosXmayor embedded refreshToken={refreshKey} />}
      </Box>

      {/* Cobrar x mayor (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={cobroOpen} onClose={() => setCobroOpen(false)}>
        <CobrosXmayor embedded onExit={() => { setCobroOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Informe de cobros (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={informeOpen} onClose={() => setInformeOpen(false)}>
        <BarraVolver onExit={() => setInformeOpen(false)} />
        <ListadoCobrosXmayor />
      </Dialog>

      {/* Informe de ingresos (contado + cobros a crédito) */}
      <Dialog fullScreen open={ingresosOpen} onClose={() => setIngresosOpen(false)}>
        <BarraVolver onExit={() => setIngresosOpen(false)} />
        <IngresosXmayor />
      </Dialog>
    </Box>
  );
}
